<template>
	<div id="clientDns" class="jc-view">
		<JuniperPageHeader :back="'/juniper/clients/edit/' + client.id" :title="client ? client.name : ''" subtitle="WordPress Import" />
		<div class="row mb-3">
			<div class="col-md-12">
				<div class="card" style="min-height:400px">
					<div class="card-body">
						<div class="row d-flex justify-content-end align-items-center">
							<div class="col-md-6">
								<div class="form-group mb-3">
									<label for="">Wordpress Website</label>
									<input class="form-control" v-model="website" type="text" />
									<div id="emailHelp" class="form-text">Enter domain in format <code class="bg-warning-lighter p-1">https://heyvile.org</code></div>
								</div>
							</div>
							<div class="col-md-6">
								<button @click="prepare()" class="btn btn-primary mb-3" :class="{ disabled: initialSubmitDisabled }">
									<font-awesome-icon v-if="prepareLoading" :icon="['fas', 'spinner']" :class="['fa-spin']" />
									Submit
								</button>
							</div>
						</div>
						<div>
							<div v-show="checkWpStarted" class="mb-3">
								<small
									><i
										>Checking for wordpress<span
											><div v-if="!checkWpEnded" class="dot-flashing d-inline-block ms-2"></div>
											<span class="ms-3" v-html="checkWpMessage"></span></span></i
								></small>
							</div>
							<!-- wp offload media warning -->
							<div v-if="wpOffloadMediaDetected" class="alert alert-warning mb-3">
								<font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-2" />
								<strong>WP Offload Media</strong> plugin detected. Please deactivate this plugin before importing.
								All import actions are disabled until the plugin is deactivated. Keep it deactivated until the import is finished, otherwise media files will be imported with issues. After import is finished, you can reactivate the plugin if you need it.
							</div>
							<!-- import posts -->
							<div v-show="checkWpPostsStarted" class="mb-3">
								<div class="d-flex align-items-center">
									<small
										><i
											>Checking for posts<span
												><div v-if="!checkWpPostsEnded" class="dot-flashing d-inline-block ms-2"></div>
												<span class="ms-3" v-html="checkWpPostsMessage"></span></span></i
									></small>
									<button
										v-if="!prepareLoading && checkWpPostsShowImportBtn"
										@click="importPosts"
										class="btn btn-sm btn-primary ms-3"
										:class="{ disabled: postsImporting }"
									>
										<font-awesome-icon v-if="postsImporting" :icon="['fas', 'spinner']" :class="['fa-spin']" />
										Import
									</button>
									<date-range-picker
										v-if="!prepareLoading && checkWpPostsShowImportBtn"
										ref="picker"
										opens="right"
										:locale-data="{ firstDay: 1, format: 'mmmm d, yyyy' }"
										:showDropdowns="true"
										v-model="dateRange"
										@update="updateValues"
										class="ms-3"
										:disable="postsImporting"
									>
										<template v-slot:input="picker" style="min-width: 350px;">
											{{ picker.startDate ? moment(picker.startDate).format('LL') : null }} -
											{{ picker.endDate ? moment(picker.endDate).format('LL') : null }}
										</template>
									</date-range-picker>
									<button
										v-if="!prepareLoading && checkWpPostsShowImportBtn && dateRange.startDate"
										@click="clearRange"
										class="btn btn-sm btn-secondary ms-3"
										:class="{ disabled: postsImporting }"
									>
										<font-awesome-icon v-if="postsImporting" :icon="['fas', 'spinner']" :class="['fa-spin']" />
										Clear
									</button>
								</div>
								<div>
									<div v-if="postsImportingShowProgress" class="d-flex">
										<div class="progress mt-2 d-inline-flex flex-grow-1">
											<div
												class="progress-bar progress-bar-striped"
												:class="{ 'progress-bar-animated': postsImporting }"
												role="progressbar"
												aria-label="Animated striped example"
												:aria-valuenow="postsImportingProgress"
												aria-valuemin="0"
												aria-valuemax="100"
												:style="`width: ${postsImportingProgress}%`"
											>
												{{ postsImportingProgress }}%
											</div>
										</div>
										<!-- cancel batch -->
										<button
											v-if="batches['wp-import-posts'] && postsImporting"
											class="btn btn-sm btn-danger ms-3"
											@click="cancelBatch(batches['wp-import-posts'].batch_id, 'wp-import-posts')"
											:disabled="importCancelling['wp-import-posts']"
										>
											<font-awesome-icon v-if="importCancelling['wp-import-posts']" :icon="['fas', 'spinner']" :class="['fa-spin']" />
											<font-awesome-icon v-else :icon="['fas', 'times']" />
										</button>
									</div>
									<div v-if="postsImportingEnded" class="mt-2">
										<div class="text-end">
											<a data-bs-toggle="collapse" href="#collapsePostsImport" role="button" aria-expanded="false" aria-controls="collapsePostsImport">
												Click to see log
											</a>
										</div>
										<div class="collapse" id="collapsePostsImport">
											<div class="card card-body">
												<p>
													Successfully imported <b>{{ postsImportingLogInfoSuccessfully }}</b>
												</p>
												<p v-if="postsImportingLogInfoFailed">
													Failed <b>{{ postsImportingLogInfoFailed }}</b>
												</p>
												<ul v-if="postsImportingLogInfoFailed">
													<li v-for="failedJob in postsImportingLogInfoFailedList" :key="`failedJob-${failedJob.id}`">
														<div>
															<i>WP ID</i>: <b>{{ failedJob.payload.data.command.post.id }}</b>
														</div>
														<div>
															<i>WP Title</i>: <b>{{ failedJob.payload.data.command.post.title.rendered }}</b>
														</div>
														<a
															class="bg-light p-2 d-block text-dark text-decoration-none"
															data-bs-toggle="collapse"
															:href="`#collapseFailedJob${failedJob.id}`"
															role="button"
															aria-expanded="false"
															:aria-controls="`collapseFailedJob${failedJob.id}`"
														>
															{{ failedJob.exception.split('\n')[0] }}
														</a>
														<div class="collapse tw-import-alert-danger p-2" :id="`collapseFailedJob${failedJob.id}`">
															{{ failedJob.exception }}
														</div>
													</li>
												</ul>
											</div>
										</div>
									</div>
								</div>
							</div>
							<!-- import pages -->
							<div v-show="checkWpPagesStarted" class="mb-3">
								<small
									><i
										>Checking for pages<span
											><div v-if="!checkWpPagesEnded" class="dot-flashing d-inline-block ms-2"></div>
											<span class="ms-3" v-html="checkWpPagesMessage"></span></span></i
								></small>
								<button
									v-if="!prepareLoading && checkWpPagesShowImportBtn"
									@click="importPages"
									class="btn btn-sm btn-primary ms-3"
									:class="{ disabled: pagesImporting }"
								>
									<font-awesome-icon v-if="pagesImporting" :icon="['fas', 'spinner']" :class="['fa-spin']" />
									Import
								</button>
								<div v-if="pagesImportingShowProgress" class="d-flex">
									<div class="progress mt-2 d-inline-flex flex-grow-1">
										<div
											class="progress-bar progress-bar-striped"
											:class="{ 'progress-bar-animated': pagesImporting }"
											role="progressbar"
											aria-label="Animated striped example"
											:aria-valuenow="pagesImportingProgress"
											aria-valuemin="0"
											aria-valuemax="100"
											:style="`width: ${pagesImportingProgress}%`"
										>
											{{ pagesImportingProgress }}%
										</div>
									</div>
									<!-- cancel batch -->
									<button
										v-if="batches['wp-import-pages'] && pagesImporting"
										class="btn btn-sm btn-danger ms-3"
										@click="cancelBatch(batches['wp-import-pages'].batch_id, 'wp-import-pages')"
										:disabled="importCancelling['wp-import-pages']"
									>
										<font-awesome-icon v-if="importCancelling['wp-import-pages']" :icon="['fas', 'spinner']" :class="['fa-spin']" />
										<font-awesome-icon v-else :icon="['fas', 'times']" />
									</button>
								</div>
								<div v-if="pagesImportingEnded" class="mt-2">
									<div class="text-end">
										<a data-bs-toggle="collapse" href="#collapsePagesImport" role="button" aria-expanded="false" aria-controls="collapsePagesImport">
											Click to see log
										</a>
									</div>
									<div class="collapse" id="collapsePagesImport">
										<div class="card card-body">
											<p>
												Successfully imported <b>{{ pagesImportingLogInfoSuccessfully }}</b>
											</p>
											<p v-if="pagesImportingLogInfoFailed">
												Failed <b>{{ pagesImportingLogInfoFailed }}</b>
											</p>
											<ul v-if="pagesImportingLogInfoFailed">
												<li v-for="failedJob in pagesImportingLogInfoFailedList" :key="`failedJob-${failedJob.id}`">
													<div>
														<i>WP ID</i>: <b>{{ failedJob.payload.data.command.page.id }}</b>
													</div>
													<div>
														<i>WP Title</i>: <b>{{ failedJob.payload.data.command.page.title.rendered }}</b>
													</div>
													<a
														class="bg-light p-2 d-block text-dark text-decoration-none"
														data-bs-toggle="collapse"
														:href="`#collapseFailedJob${failedJob.id}`"
														role="button"
														aria-expanded="false"
														:aria-controls="`collapseFailedJob${failedJob.id}`"
														v-html="failedJob.exception.split('\n')[0]"
													></a>
													<div class="collapse tw-import-alert-danger p-2" :id="`collapseFailedJob${failedJob.id}`" v-html="failedJob.exception"></div>
												</li>
											</ul>
										</div>
									</div>
								</div>
							</div>
							<!-- import media -->
							<div v-show="checkWpMediaStarted" class="mb-3">
								<small
									><i
										>Checking for media<span
											><div v-if="!checkWpMediaEnded" class="dot-flashing d-inline-block ms-2"></div>
											<span class="ms-3" v-html="checkWpMediaMessage"></span></span></i
								></small>
								<button
									v-if="!prepareLoading && checkWpMediaShowImportBtn"
									@click="importMedia"
									class="btn btn-sm btn-primary ms-3"
									:class="{ disabled: mediaImporting }"
								>
									<font-awesome-icon v-if="mediaImporting" :icon="['fas', 'spinner']" :class="['fa-spin']" />
									Import
								</button>
								<input class="form-control d-inline-flex w-auto ms-2" type="text" v-model="importMediaInclude" />
								<div v-if="mediaImportingShowProgress" class="d-flex">
									<div class="progress mt-2 d-inline-flex flex-grow-1">
										<div
											class="progress-bar progress-bar-striped"
											:class="{ 'progress-bar-animated': mediaImporting }"
											role="progressbar"
											aria-label="Animated striped example"
											:aria-valuenow="mediaImportingProgress"
											aria-valuemin="0"
											aria-valuemax="100"
											:style="`width: ${mediaImportingProgress}%`"
										>
											{{ mediaImportingProgress }}%
										</div>
									</div>
									<!-- cancel batch -->
									<button
										v-if="batches['wp-import-media'] && mediaImporting"
										class="btn btn-sm btn-danger ms-3"
										@click="cancelBatch(batches['wp-import-media'].batch_id, 'wp-import-media')"
										:disabled="importCancelling['wp-import-media']"
									>
										<font-awesome-icon v-if="importCancelling['wp-import-media']" :icon="['fas', 'spinner']" :class="['fa-spin']" />
										<font-awesome-icon v-else :icon="['fas', 'times']" />
									</button>
								</div>
								<div v-if="mediaImportingEnded" class="mt-2">
									<div class="text-end">
										<a data-bs-toggle="collapse" href="#collapseMediaImport" role="button" aria-expanded="false" aria-controls="collapseMediaImport">
											Click to see log
										</a>
									</div>
									<div class="collapse" id="collapseMediaImport">
										<div class="card card-body">
											<p>
												Successfully imported <b>{{ mediaImportingLogInfoSuccessfully }}</b>
											</p>
											<p v-if="mediaImportingLogInfoFailed">
												Failed <b>{{ mediaImportingLogInfoFailed }}</b>
											</p>
											<ul v-if="mediaImportingLogInfoFailed">
												<li v-for="failedJob in mediaImportingLogInfoFailedList" :key="`failedJob-${failedJob.id}`">
													<div>
														<i>WP ID</i>: <b>{{ failedJob.payload.data.command.id }}</b>
													</div>
													<div>
														<i>WP Title</i>: <b>{{ failedJob.payload.data.command.title.rendered }}</b>
													</div>
													<a
														class="bg-light p-2 d-block text-dark text-decoration-none"
														data-bs-toggle="collapse"
														:href="`#collapseFailedJob${failedJob.id}`"
														role="button"
														aria-expanded="false"
														:aria-controls="`collapseFailedJob${failedJob.id}`"
														v-html="failedJob.exception.split('\n')[0]"
													></a>
													<div class="collapse tw-import-alert-danger p-2" :id="`collapseFailedJob${failedJob.id}`" v-html="failedJob.exception"></div>
												</li>
											</ul>
										</div>
									</div>
								</div>
							</div>
							<!-- import faqs -->
							<div v-show="checkWpFaqsStarted" class="mb-3">
								<small
									><i
										>Checking for faqs<span
											><div v-if="!checkWpFaqsEnded" class="dot-flashing d-inline-block ms-2"></div>
											<span class="ms-3" v-html="checkWpFaqsMessage"></span></span></i
								></small>
								<button v-if="!prepareLoading && checkWpFaqsShowImportBtn" @click="prepare()" class="btn btn-sm btn-primary ms-3 disabled">
									<font-awesome-icon v-if="faqsImporting" :icon="['fas', 'spinner']" :class="['fa-spin']" />
									Import
								</button>
							</div>
							<!-- import departments -->
							<div v-show="checkWpDepartmentsStarted" class="mb-3">
								<small
									><i
										>Checking for departments<span
											><div v-if="!checkWpDepartmentsEnded" class="dot-flashing d-inline-block ms-2"></div>
											<span class="ms-3" v-html="checkWpDepartmentsMessage"></span></span></i
								></small>
								<button v-if="!prepareLoading && checkWpDepartmentsShowImportBtn" @click="prepare()" class="btn btn-sm btn-primary ms-3 disabled">
									<font-awesome-icon v-if="departmentsImporting" :icon="['fas', 'spinner']" :class="['fa-spin']" />
									Import
								</button>
							</div>
							<!-- import meeting repository -->
							<div v-show="checkWpMeetingRepositoryStarted" class="mb-3">
								<small
									><i
										>Checking for meeting repository<span
											><div v-if="!checkWpMeetingRepositoryEnded" class="dot-flashing d-inline-block ms-2"></div>
											<span class="ms-3" v-html="checkWpMeetingRepositoryMessage"></span></span></i
								></small>
								<button v-if="!prepareLoading && checkWpMeetingRepositoryShowImportBtn" @click="prepare()" class="btn btn-sm btn-primary ms-3 disabled">
									<font-awesome-icon v-if="meetingRepositoryImporting" :icon="['fas', 'spinner']" :class="['fa-spin']" />
									Import
								</button>
							</div>
							<!-- import tribe events -->
							<div v-show="checkWpEventsStarted" class="mb-3">
								<small
									><i
										>Checking for tribe events<span
											><div v-if="!checkWpEventsEnded" class="dot-flashing d-inline-block ms-2"></div>
											<span class="ms-3" v-html="checkWpEventsMessage"></span></span></i
								></small>
								<button v-if="!prepareLoading && checkWpEventsShowImportBtn" @click="prepare()" class="btn btn-sm btn-primary ms-3 disabled">
									<font-awesome-icon v-if="eventsImporting" :icon="['fas', 'spinner']" :class="['fa-spin']" />
									Import
								</button>
							</div>
							<!-- import subscribers -->
							<div v-show="checkWpSubscribersStarted" class="mb-3">
								<small
									><i
										>Checking for subscribers<span
											><div v-if="!checkWpSubscribersEnded" class="dot-flashing d-inline-block ms-2"></div>
											<span class="ms-3" v-html="checkWpSubscribersMessage"></span></span></i
								></small>
								<button v-if="!prepareLoading && checkWpSubscribersShowImportBtn" @click="prepare()" class="btn btn-sm btn-primary ms-3 disabled">
									<font-awesome-icon v-if="subscribersImporting" :icon="['fas', 'spinner']" :class="['fa-spin']" />
									Import
								</button>
							</div>
							<div v-show="prepareResult" v-html="prepareResult"></div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import juniperApi from '@/api/juniperApi'
//import restApi from '../../../api/index'
import axios from 'axios'
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'

export default {
	name: 'ClientWPImport',
	data() {
		return {
			loading: false,
			client: {},
			website: '',
			prepareResult: false,
			prepareLoading: false,
			checkWpStarted: false,
			checkWpEnded: false,
			checkWpMessage: 'Please wait.',
			initialSubmitDisabled: true,
			importMonitoringOngoing: false,
			batches: {},
			importCancelling: {},
			wpOffloadMediaDetected: false,
			// posts
			checkWpPostsStarted: false,
			checkWpPostsEnded: false,
			checkWpPostsMessage: 'Please wait.',
			checkWpPostsShowImportBtn: false,
			postsImporting: false,
			postsImportingEnded: false,
			postsImportingProgress: 0,
			postsImportingShowProgress: false,
			postsImportingProgressGetTries: 0,
			postsImportingLogInfoSuccessfully: 0,
			postsImportingLogInfoFailed: 0,
			postsImportingLogInfoFailedList: [],
			// pages
			checkWpPagesStarted: false,
			checkWpPagesEnded: false,
			checkWpPagesMessage: 'Please wait.',
			checkWpPagesShowImportBtn: false,
			pagesImporting: false,
			pagesImportingEnded: false,
			pagesImportingProgress: 0,
			pagesImportingShowProgress: false,
			pagesImportingProgressGetTries: 0,
			pagesImportingLogInfoSuccessfully: 0,
			pagesImportingLogInfoFailed: 0,
			pagesImportingLogInfoFailedList: [],
			// media
			checkWpMediaStarted: false,
			checkWpMediaEnded: false,
			checkWpMediaMessage: 'Please wait.',
			checkWpMediaShowImportBtn: false,
			mediaImporting: false,
			mediaImportingEnded: false,
			mediaImportingProgress: 0,
			mediaImportingShowProgress: false,
			mediaImportingProgressGetTries: 0,
			mediaImportingLogInfoSuccessfully: 0,
			mediaImportingLogInfoFailed: 0,
			mediaImportingLogInfoFailedList: [],
			importMediaInclude: null,
			// faqs
			checkWpFaqsStarted: false,
			checkWpFaqsEnded: false,
			checkWpFaqsMessage: 'Please wait.',
			checkWpFaqsShowImportBtn: false,
			faqsImporting: false,
			// departments
			checkWpDepartmentsStarted: false,
			checkWpDepartmentsEnded: false,
			checkWpDepartmentsMessage: 'Please wait.',
			checkWpDepartmentsShowImportBtn: false,
			departmentsImporting: false,
			// meeting repository
			checkWpMeetingRepositoryStarted: false,
			checkWpMeetingRepositoryEnded: false,
			checkWpMeetingRepositoryMessage: 'Please wait.',
			checkWpMeetingRepositoryShowImportBtn: false,
			meetingRepositoryImporting: false,
			// events
			checkWpEventsStarted: false,
			checkWpEventsEnded: false,
			checkWpEventsMessage: 'Please wait.',
			checkWpEventsShowImportBtn: false,
			eventsImporting: false,
			// subscribers
			checkWpSubscribersStarted: false,
			checkWpSubscribersEnded: false,
			checkWpSubscribersMessage: 'Please wait.',
			checkWpSubscribersShowImportBtn: false,
			subscribersImporting: false,
			// date range picker
			dateRange: {
				startDate: null,
				endDate: null } }
	},
	components: { DateRangePicker },
	computed: {
		...mapState(['authUser']),
		...mapGetters(['isAuth']) },

	async created() {
		this.getClient()
		this.checkOngoingImports()
	},
	methods: {
		// Get client
		getClient() {
			this.loading = true
			juniperApi
				.get('/clients/' + this.$route.params.id)
				.then(({ data }) => {
					this.client = data
					this.loading = false
				})
				.catch(error => {
					this.loading = false
					console.log(error)
				})
		},
		async prepare() {
			this.resetStatuses()
			if (this.client.staging) {
				this.prepareLoading = true
				this.checkWpStarted = true

				// initial wordpress check
				try {
					let wpJsonRes = await axios.get(`${this.website}/wp-json`, {})
					this.checkWpMessage = '<span class="text-success fw-bold">OK</span>'
					this.checkWpEnded = true

					// check for WP Offload Media plugin
					if (wpJsonRes.data && wpJsonRes.data.namespaces && wpJsonRes.data.namespaces.includes('wp-offload-media/v1')) {
						this.wpOffloadMediaDetected = true
						this.prepareLoading = false
						return
					}

					// initial posts check
					this.checkWpPostsStarted = true
					try {
						let res = await axios.head(`${this.website}/wp-json/wp/v2/posts`, {})
						this.checkWpPostsMessage = `<span class="text-success fw-bold">OK</span> <span>${res.headers['x-wp-total']}</span>`
						this.checkWpPostsEnded = true
						this.checkWpPostsShowImportBtn = true
					} catch (error) {
						this.checkWpPostsEnded = true
						this.checkWpPostsMessage = '<span class="text-danger fw-bold">FAIL</span>'
						console.log(error)
					}
					// initial pages check
					this.checkWpPagesStarted = true
					try {
						let res = await axios.head(`${this.website}/wp-json/wp/v2/pages`, {})
						this.checkWpPagesMessage = `<span class="text-success fw-bold">OK</span> <span>${res.headers['x-wp-total']}</span>`
						this.checkWpPagesEnded = true
						this.checkWpPagesShowImportBtn = true
					} catch (error) {
						this.checkWpPagesEnded = true
						this.checkWpPagesMessage = '<span class="text-danger fw-bold">FAIL</span>'
						console.log(error)
					}
					// initial media check
					this.checkWpMediaStarted = true
					try {
						let res = await axios.head(`${this.website}/wp-json/wp/v2/media`, {})
						this.checkWpMediaMessage = `<span class="text-success fw-bold">OK</span> <span>${res.headers['x-wp-total']}</span>`
						this.checkWpMediaEnded = true
						this.checkWpMediaShowImportBtn = true
					} catch (error) {
						this.checkWpMediaEnded = true
						this.checkWpMediaMessage = '<span class="text-danger fw-bold">FAIL</span>'
						console.log(error)
					}
					// initial faqs check
					this.checkWpFaqsStarted = true
					try {
						let res = await axios.head(`${this.website}/wp-json/wp/v2/tw_faqs`, {})
						this.checkWpFaqsMessage = `<span class="text-success fw-bold">OK</span> <span>${res.headers['x-wp-total']}</span>`
						this.checkWpFaqsEnded = true
						this.checkWpFaqsShowImportBtn = true
					} catch (error) {
						this.checkWpFaqsEnded = true
						this.checkWpFaqsMessage = '<span class="text-danger fw-bold">FAIL</span>'
						console.log(error)
					}
					// initial departments check
					this.checkWpDepartmentsStarted = true
					try {
						let res = await axios.head(`${this.website}/wp-json/wp/v2/departments`, {})
						this.checkWpDepartmentsMessage = `<span class="text-success fw-bold">OK</span> <span>${res.headers['x-wp-total']}</span>`
						this.checkWpDepartmentsEnded = true
						this.checkWpDepartmentsShowImportBtn = true
					} catch (error) {
						this.checkWpDepartmentsEnded = true
						this.checkWpDepartmentsMessage = '<span class="text-danger fw-bold">FAIL</span>'
						console.log(error)
					}
					// initial meeting repository check
					this.checkWpMeetingRepositoryStarted = true
					try {
						let res = await axios.head(`${this.website}/wp-json/wp/v2/twd_repository`, {})
						this.checkWpMeetingRepositoryMessage = `<span class="text-success fw-bold">OK</span> <span>${res.headers['x-wp-total']}</span>`
						this.checkWpMeetingRepositoryEnded = true
						this.checkWpMeetingRepositoryShowImportBtn = true
					} catch (error) {
						this.checkWpMeetingRepositoryEnded = true
						this.checkWpMeetingRepositoryMessage = '<span class="text-danger fw-bold">FAIL</span>'
						console.log(error)
					}
					// initial tribe events check
					this.checkWpEventsStarted = true
					try {
						let res = await axios.get(`${this.website}/wp-json/tribe/events/v1/events`, { params: { per_page: 1 } })
						this.checkWpEventsMessage = `<span class="text-success fw-bold">OK</span> <span>${res.data.total}</span>`
						this.checkWpEventsEnded = true
						this.checkWpEventsShowImportBtn = true
					} catch (error) {
						this.checkWpEventsEnded = true
						this.checkWpEventsMessage = '<span class="text-danger fw-bold">FAIL</span>'
						console.log(error)
					}
					// initial subscribers check
					this.checkWpSubscribersStarted = true
					try {
						let res = await axios.head(`${this.website}/wp-json/wp/v2/twd_subscribe`, { auth: { username: 'twdmwp', password: '8AmldG8Y3f9WGxYtgyi7cluHZ' } })
						this.checkWpSubscribersMessage = `<span class="text-success fw-bold">OK</span> <span>${res.headers['x-wp-total']}</span>`
						this.checkWpSubscribersEnded = true
						this.checkWpSubscribersShowImportBtn = true
					} catch (error) {
						this.checkWpSubscribersEnded = true
						this.checkWpSubscribersMessage = '<span class="text-danger fw-bold">FAIL</span>'
						console.log(error)
					}
					this.prepareLoading = false
				} catch (error) {
					this.checkWpMessage = '<span class="text-danger fw-bold">FAIL</span>'
					this.prepareLoading = false
					this.checkWpEnded = true
					this.prepareResult = 'Cannot proceed. Provided website is not wordpress site or worpdress api is disabled.'
					console.log(error)
				}
			} else {
				this.prepareResult = 'Cannot do import on production websites.'
				this.prepareLoading = false
			}
		},
		resetStatuses() {
			this.prepareResult = false
			this.wpOffloadMediaDetected = false
			this.checkWpStarted = false
			this.checkWpEnded = false
			this.checkWpMessage = 'Please wait.'
			// posts
			this.checkWpPostsStarted = false
			this.checkWpPostsEnded = false
			this.checkWpPostsMessage = 'Please wait.'
			this.checkWpPostsShowImportBtn = false
			this.postsImporting = false
			this.postsImportingEnded = false
			this.postsImportingProgress = 0
			this.postsImportingProgressGetTries = 0
			this.postsImportingShowProgress = false
			this.postsImportingLogInfoSuccessfully = 0
			this.postsImportingLogInfoFailed = 0
			this.postsImportingLogInfoFailedList = []
			// pages
			this.checkWpPagesStarted = false
			this.checkWpPagesEnded = false
			this.checkWpPagesMessage = 'Please wait.'
			this.checkWpPagesShowImportBtn = false
			this.pagesImporting = false
			;(this.pagesImportingEnded = false),
				(this.pagesImportingProgress = 0),
				(this.pagesImportingShowProgress = false),
				(this.pagesImportingProgressGetTries = 0),
				(this.pagesImportingLogInfoSuccessfully = 0),
				(this.pagesImportingLogInfoFailed = 0),
				(this.pagesImportingLogInfoFailedList = []),
				// media
				(this.checkWpMediaStarted = false)
			this.checkWpMediaEnded = false
			this.checkWpMediaMessage = 'Please wait.'
			this.checkWpMediaShowImportBtn = false
			this.mediaImporting = false
			this.mediaImportingEnded = false
			this.mediaImportingProgress = 0
			this.mediaImportingShowProgress = false
			this.mediaImportingProgressGetTries = 0
			this.mediaImportingLogInfoSuccessfully = 0
			this.mediaImportingLogInfoFailed = 0
			this.mediaImportingLogInfoFailedList = []
			// faqs
			this.checkWpFaqsStarted = false
			this.checkWpFaqsEnded = false
			this.checkWpFaqsMessage = 'Please wait.'
			this.checkWpFaqsShowImportBtn = false
			this.faqsImporting = false
			// departments
			this.checkWpDepartmentsStarted = false
			this.checkWpDepartmentsEnded = false
			this.checkWpDepartmentsMessage = 'Please wait.'
			this.checkWpDepartmentsShowImportBtn = false
			this.departmentsImporting = false
			// meeting repository
			this.checkWpMeetingRepositoryStarted = false
			this.checkWpMeetingRepositoryEnded = false
			this.checkWpMeetingRepositoryMessage = 'Please wait.'
			this.checkWpMeetingRepositoryShowImportBtn = false
			this.meetingRepositoryImporting = false
			// tribe events
			this.checkWpEventsStarted = false
			this.checkWpEventsEnded = false
			this.checkWpEventsMessage = 'Please wait.'
			this.checkWpEventsShowImportBtn = false
			this.eventsImporting = false
			// subscribers
			this.checkWpSubscribersStarted = false
			this.checkWpSubscribersEnded = false
			this.checkWpSubscribersMessage = 'Please wait.'
			this.checkWpSubscribersShowImportBtn = false
			this.subscribersImporting = false
			// date range picker
			this.dateRange = {
				startDate: null,
				endDate: null }
		},
		async importPosts() {
			this.$swal({
				title: 'Are you sure?',
				showCancelButton: true,
				icon: 'warning',
				html: `Are you sure you want to import wordpress news and notices?`,
				confirmButtonText: 'Yes, do it!',
				showLoaderOnConfirm: true,
				toast: false,
				position: 'center',
				showConfirmButton: true,
				timer: undefined,
				timerProgressBar: false,
				confirmButtonColor: '#0FCB4F',
				cancelButtonColor: '#EC2641',
				allowOutsideClick: () => !this.$swal.isLoading() }).then(async result => {
				if (result.isConfirmed) {
					this.postsImporting = true
					this.initialSubmitDisabled = true
					this.postsImportingProgress = 0
					await juniperApi.post(`/wordpress/import-posts`, {
						website: this.website,
						client_id: this.$route.params.id,
						after: this.dateRange.startDate ? this.moment(this.dateRange.startDate).format('YYYY-MM-DDT00:00:00Z') : null,
						before: this.dateRange.endDate ? this.moment(this.dateRange.endDate).format('YYYY-MM-DDT23:59:59Z') : null })
					if (!this.importMonitoringOngoing) {
						this.importMonitoring()
					}
				}
			})
		},
		async importPages() {
			this.$swal({
				title: 'Are you sure?',
				showCancelButton: true,
				icon: 'warning',
				html: `Are you sure you want to import wordpress pages?`,
				confirmButtonText: 'Yes, do it!',
				showLoaderOnConfirm: true,
				toast: false,
				position: 'center',
				showConfirmButton: true,
				timer: undefined,
				timerProgressBar: false,
				confirmButtonColor: '#0FCB4F',
				cancelButtonColor: '#EC2641',
				allowOutsideClick: () => !this.$swal.isLoading() }).then(async result => {
				if (result.isConfirmed) {
					this.pagesImporting = true
					this.initialSubmitDisabled = true
					this.pagesImportingProgress = 0
					let response = await juniperApi.post(`/wordpress/import-pages`, { website: this.website, client_id: this.$route.params.id })
					if (response && response.data) {
						this.batches = { ...this.batches, ...response.data }
					}
					if (!this.importMonitoringOngoing) {
						this.importMonitoring()
					}
				}
			})
		},
		importMedia() {
			this.$swal({
				title: 'Are you sure?',
				showCancelButton: true,
				icon: 'warning',
				html: `Are you sure you want to import wordpress media?`,
				confirmButtonText: 'Yes, do it!',
				showLoaderOnConfirm: true,
				toast: false,
				position: 'center',
				showConfirmButton: true,
				timer: undefined,
				timerProgressBar: false,
				confirmButtonColor: '#0FCB4F',
				cancelButtonColor: '#EC2641',
				allowOutsideClick: () => !this.$swal.isLoading() }).then(async result => {
				if (result.isConfirmed) {
					this.mediaImporting = true
					this.initialSubmitDisabled = true
					this.mediaImportingProgress = 0
					let response = await juniperApi.post(`/wordpress/import-media`, { website: this.website, client_id: this.$route.params.id, include: this.importMediaInclude })
					if (response && response.data) {
						// if in response skipped media are provided, show them in alert
						if (response.data['wp-import-media'] && response.data['wp-import-media'].skipped_media_count) {
							let skippedInfo = (response.data['wp-import-media'].skipped_media_count + ' media were skipped during import because they are video or audio files.\n' + response.data['wp-import-media'].skipped_media_message).replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, '')
							this.$swal({
								title: 'Skipped media',
								html: '<b>' + response.data['wp-import-media'].skipped_media_count + '</b> media were skipped during import because they are video or audio files.<br>' + response.data['wp-import-media'].skipped_media_message,
								icon: 'info',
								position: 'bottom-end',
								toast: true,
								timerProgressBar: false,
								timer: false,
								showConfirmButton: true,
								confirmButtonText: 'Copy Info',
								showDenyButton: true,
								denyButtonText: 'Close',
								denyButtonColor: '#6c757d',
								width: '60%',
								heightAuto: false,
								customClass: { htmlContainer: 'swal2-html-scrollable' } }).then(result => {
								if (result.isConfirmed) {
									navigator.clipboard.writeText(skippedInfo).then(() => {
										this.$swal({ title: 'Copied to clipboard!', icon: 'success', toast: true, position: 'bottom-end', timer: 2000, showConfirmButton: false })
									})
								}
							})
						}
						// remove skipped_media_count and skipped_media_message from batches data to avoid confusion
						if (response.data['wp-import-media']) {
							delete response.data['wp-import-media'].skipped_media_count
							delete response.data['wp-import-media'].skipped_media_message
						}
						this.batches = { ...this.batches, ...response.data }
					}
					if (!this.importMonitoringOngoing) {
						this.importMonitoring()
					}
				}
			})
		},
		async importMonitoring() {
			this.importMonitoringOngoing = true
			await this.delay(2000) // Waits for 2 seconds
			let batches = await juniperApi.post(`/wordpress/import-monitoring`, { client_id: this.$route.params.id })

			if (
				Object.entries(batches.data).length ||
				(!Object.entries(batches.data).length &&
					(this.postsImportingProgressGetTries < 3 ||
						this.pagesImportingProgressGetTries < 3 ||
						(this.postsImportingProgress && this.postsImportingProgress < 100) ||
						(this.pagesImportingProgress && this.pagesImportingProgress < 100)))
			) {
				this.batches = { ...this.batches, ...batches.data }

				if (batches.data['wp-import-posts']) {
					this.postsImportingShowProgress = true

					this.postsImportingProgress = Math.floor(
						((batches.data['wp-import-posts']['total_jobs'] - (batches.data['wp-import-posts']['pending_jobs'] - batches.data['wp-import-posts']['failed_jobs'])) /
							batches.data['wp-import-posts']['total_jobs']) *
							100
					)
				} else {
					if (this.postsImportingProgress) {
						this.postsImportingProgress = 100
					}
				}

				if (batches.data['wp-import-pages']) {
					this.pagesImportingShowProgress = true

					this.pagesImportingProgress = Math.floor(
						((batches.data['wp-import-pages']['total_jobs'] - (batches.data['wp-import-pages']['pending_jobs'] - batches.data['wp-import-pages']['failed_jobs'])) /
							batches.data['wp-import-pages']['total_jobs']) *
							100
					)
				} else {
					if (this.pagesImportingProgress) {
						this.pagesImportingProgress = 100
					}
				}

				if (batches.data['wp-import-media']) {
					this.mediaImportingShowProgress = true

					this.mediaImportingProgress = Math.floor(
						((batches.data['wp-import-media']['total_jobs'] - (batches.data['wp-import-media']['pending_jobs'] - batches.data['wp-import-media']['failed_jobs'])) /
							batches.data['wp-import-media']['total_jobs']) *
							100
					)
				} else {
					if (this.mediaImportingProgress) {
						this.mediaImportingProgress = 100
					}
				}

				this.postsImportingProgressGetTries++
				this.pagesImportingProgressGetTries++
				this.importMonitoring()
			} else {
				this.postsImportingProgressGetTries = 0
				if (this.postsImporting) {
					this.postsImportingShowProgress = true
					this.postsImportingProgress = 100
					let postsImportingLogInfo = await juniperApi.post(`/wordpress/import-log`, { client_id: this.$route.params.id, batch_name: 'wp-import-posts' })

					this.postsImportingLogInfoSuccessfully = postsImportingLogInfo.data.successful
					this.postsImportingLogInfoFailed = postsImportingLogInfo.data.failed
					this.postsImportingLogInfoFailedList = postsImportingLogInfo.data.failed_list
					this.postsImportingEnded = true
					this.postsImporting = false
				}

				if (this.pagesImporting) {
					this.pagesImportingShowProgress = true
					this.pagesImportingProgress = 100
					let pagesImportingLogInfo = await juniperApi.post(`/wordpress/import-log`, { client_id: this.$route.params.id, batch_name: 'wp-import-pages' })

					this.pagesImportingLogInfoSuccessfully = pagesImportingLogInfo.data.successful
					this.pagesImportingLogInfoFailed = pagesImportingLogInfo.data.failed
					this.pagesImportingLogInfoFailedList = pagesImportingLogInfo.data.failed_list
					this.pagesImportingEnded = true
					this.pagesImporting = false
				}

				if (this.mediaImporting) {
					this.mediaImportingShowProgress = true
					this.mediaImportingProgress = 100
					let mediaImportingLogInfo = await juniperApi.post(`/wordpress/import-log`, { client_id: this.$route.params.id, batch_name: 'wp-import-media' })

					this.mediaImportingLogInfoSuccessfully = mediaImportingLogInfo.data.successful
					this.mediaImportingLogInfoFailed = mediaImportingLogInfo.data.failed
					this.mediaImportingLogInfoFailedList = mediaImportingLogInfo.data.failed_list
					this.mediaImportingEnded = true
					this.mediaImporting = false
				}

				// if all imports are done, import ongoing status is false
				if (!this.postsImporting && !this.pagesImporting && !this.mediaImporting) {
					this.importMonitoringOngoing = false
					this.initialSubmitDisabled = false
				}
			}

			return batches
		},
		async checkOngoingImports() {
			let batches = await this.importMonitoring()

			if (Object.entries(batches.data).length) {
				if (batches.data['wp-import-posts']) {
					this.checkWpPostsStarted = true
					this.checkWpPostsMessage = 'Import ongoing'
					this.postsImporting = true
				}
				if (batches.data['wp-import-pages']) {
					this.checkWpPagesStarted = true
					this.checkWpPagesMessage = 'Import ongoing'
					this.pagesImporting = true
				}
				if (batches.data['wp-import-media']) {
					this.checkWpMediaStarted = true
					this.checkWpMediaMessage = 'Import ongoing'
					this.mediaImporting = true
				}
			} else {
				this.initialSubmitDisabled = false
			}
		},
		delay(ms) {
			return new Promise(resolve => setTimeout(resolve, ms))
		},
		async cancelBatch(batchId, type) {
			this.importCancelling[type] = true

			this.$swal({
				title: 'Are you sure?',
				showCancelButton: true,
				icon: 'warning',
				html: `Are you sure you want to cancel import?`,
				confirmButtonText: 'Yes, do it!',
				showLoaderOnConfirm: true,
				toast: false,
				position: 'center',
				showConfirmButton: true,
				timer: undefined,
				timerProgressBar: false,
				confirmButtonColor: '#0FCB4F',
				cancelButtonColor: '#EC2641',
				allowOutsideClick: () => !this.$swal.isLoading() }).then(async result => {
				if (result.isConfirmed) {
					let response = await juniperApi.post(`/wordpress/import-cancel`, { client_id: this.$route.params.id, batch_id: batchId })
					if (response && response.status === 200) {
						delete this.batches[type]
						this.checkOngoingImports()
					}
				}
				this.importCancelling[type] = false
			})
		},
		async updateValues(range) {
			// posts
			this.checkWpPostsStarted = false
			this.checkWpPostsEnded = false
			this.checkWpPostsMessage = 'Please wait.'
			this.checkWpPostsShowImportBtn = false
			this.postsImporting = false
			this.postsImportingEnded = false
			this.postsImportingProgress = 0
			this.postsImportingProgressGetTries = 0
			this.postsImportingShowProgress = false
			this.postsImportingLogInfoSuccessfully = 0
			this.postsImportingLogInfoFailed = 0
			this.postsImportingLogInfoFailedList = []
			// this.dateRange.startDate = range.start;
			// this.dateRange.endDate = range.end;
			// recalculate posts
			this.checkWpPostsStarted = true
			try {
				let res
				if (range.startDate && range.endDate) {
					res = await axios.head(`${this.website}/wp-json/wp/v2/posts`, {
						params: { after: this.moment(range.startDate).format('YYYY-MM-DDT00:00:00Z'), before: this.moment(range.endDate).format('YYYY-MM-DDT23:59:59Z') } })
				} else {
					res = await axios.head(`${this.website}/wp-json/wp/v2/posts`, {})
				}

				this.checkWpPostsMessage = `<span class="text-success fw-bold">OK</span> <span>${res.headers['x-wp-total']}</span>`
				this.checkWpPostsEnded = true
				this.checkWpPostsShowImportBtn = true
			} catch (error) {
				this.checkWpPostsEnded = true
				this.checkWpPostsMessage = '<span class="text-danger fw-bold">FAIL</span>'
				console.log(error)
			}
		},
		clearRange() {
			this.dateRange = {
				startDate: null,
				endDate: null }
			this.updateValues(this.dateRange)
		} } }
</script>
<style scoped>
#clientDns {
	padding: 0 0 60px;
	margin-top: 0 !important;
}

.jc-view {
	width: 100%;
}

.tw-import-alert-danger {
	color: #8c3233 !important;
	background-color: #fbdddd !important;
	border-color: #f9cccc !important;
}
</style>
<style>
.swal2-html-scrollable {
	max-height: 300px;
	overflow-y: auto;
}
</style>
